import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Divider, Row, Typography } from 'antd';
import { TbArrowRight, TbArrowUpRight, TbCalendarClock, TbCircleDashedCheck, TbInfoCircle } from 'react-icons/tb';
import { useLocation } from 'react-router-dom';

import { BillingInterval, PLAN_CONFIG, PlanKey } from '@/constants';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { useCheckout } from '@/hooks/useStripeCheckout';
import { EMAIL_LINKS } from '@/shared/constants/externalLinks';
import { useUserStore } from '@/stores';
import { UserPlan } from '@/types';
import { getRedirectFromSearch } from '@/utilities';

import PlatformAlert from '../../alerts/PlatformAlert';
import SuccessfulCheckoutAlert from '../../alerts/SuccessfulCheckoutAlert';
import CardSeatMeter from '../../cards/CardSeatMeter';
import BillingIntervalToggle from './BillingIntervalToggle';
import CheckoutButton from './CheckoutButton';
import CostPreview from './CostPreview';
import PlanSelector from './PlanSelector';
import { calculateEducationDiscountPreview, getEducationDiscountStatus, isEducationEligible } from './pricing';
import SeatQuantityInput from './SeatQuantityInput';

export interface PlanCheckoutProps {
    isOnboarding: boolean;
    isTrialExpired?: boolean;
    onSeatsChange?: (seats: number) => void;
}

const normalizePlanKey = (value?: string | null): PlanKey | undefined => {
    const normalizedValue = value?.trim().toLowerCase();

    if (!normalizedValue) {
        return undefined;
    }
    if (normalizedValue.includes('premium')) {
        return 'premium';
    }
    if (normalizedValue.includes('pro')) {
        return 'pro';
    }
    if (normalizedValue.includes('plus')) {
        return 'plus';
    }
    if (normalizedValue.includes('enterprise')) {
        return 'enterprise';
    }

    return undefined;
};

const PlanCheckout: FC<PlanCheckoutProps> = ({ isOnboarding, isTrialExpired = false, onSeatsChange }): JSX.Element => {
    const [freeTrialLoading, setFreeTrialLoading] = useState(false);
    const checkoutPending = useRef(false);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const planFromQuery = normalizePlanKey(searchParams.get('plan'));
    const appliedPlanQuery = useRef(planFromQuery);
    const redirectPath = getRedirectFromSearch(location.search);
    const { selectedUser } = useUserStore();
    const { token } = useAntDesignTheme();
    const { mapToUserPlan } = useMaps();
    const [plan, setPlan] = useState<PlanKey>(planFromQuery ?? 'plus');
    const [interval, setInterval] = useState<BillingInterval>('monthly');
    const [seats, setSeats] = useState(() => {
        if (!isOnboarding) {
            return 1;
        }

        const storedSeats = Number(localStorage.getItem('workspaceSeats'));

        return Number.isFinite(storedSeats) && storedSeats > 0 ? storedSeats : 1;
    });
    const { createCheckout, loading } = useCheckout();

    useEffect(() => {
        if (!planFromQuery || appliedPlanQuery.current === planFromQuery) {
            return;
        }

        appliedPlanQuery.current = planFromQuery;
        setPlan(planFromQuery);
    }, [planFromQuery]);

    useEffect(() => {
        if (!isOnboarding) {
            return;
        }

        localStorage.setItem('workspaceSeats', String(seats));
        onSeatsChange?.(seats);
    }, [isOnboarding, onSeatsChange, seats]);

    const userPlanValue = useMemo(() => mapToUserPlan(plan, interval), [interval, mapToUserPlan, plan]);
    const educationEligible = isEducationEligible(selectedUser?.emailAddress);
    const educationDiscountStatus = getEducationDiscountStatus(
        selectedUser?.emailAddress,
        selectedUser?.emailConfirmed,
    );
    const educationDiscountPreview = useMemo(
        () => calculateEducationDiscountPreview(plan, interval, seats, educationDiscountStatus),
        [educationDiscountStatus, interval, plan, seats],
    );
    const checkoutPlan = useMemo(() => {
        if (!educationDiscountPreview.hasDiscount) {
            return userPlanValue;
        }

        return interval === 'monthly' ? UserPlan.EDUCATION_MONTHLY : UserPlan.EDUCATION_ANNUALLY;
    }, [educationDiscountPreview.hasDiscount, interval, userPlanValue]);

    const handleCheckout = async () => {
        if (checkoutPending.current) {
            return;
        }
        if (plan === 'enterprise') {
            window.location.href = EMAIL_LINKS.enterprise;

            return;
        }

        checkoutPending.current = true;
        try {
            await createCheckout({ isOnboarding, plan: checkoutPlan, redirect: redirectPath, seats });
        } catch {
            // The shared checkout hook reports the failure and displays the recovery toast.
        } finally {
            checkoutPending.current = false;
        }
    };

    const handleContinueWithFreeTrial = useCallback(async () => {
        if (checkoutPending.current) {
            return;
        }
        checkoutPending.current = true;
        try {
            setFreeTrialLoading(true);
            await createCheckout({
                isOnboarding,
                plan: UserPlan.FREE,
                redirect: redirectPath,
                seats: 1,
            });
        } catch {
            // The shared checkout hook owns error reporting for both paid and free checkout.
        } finally {
            checkoutPending.current = false;
            setFreeTrialLoading(false);
        }
    }, [createCheckout, isOnboarding, redirectPath]);

    return (
        <>
            <SuccessfulCheckoutAlert />
            {!isOnboarding && !isTrialExpired ? (
                <>
                    <Typography.Title id='settings-account-plans-seats-section' level={4}>
                        Seats
                    </Typography.Title>
                    <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                        View, upgrade, and manage your seats below.
                    </Typography.Paragraph>
                    <CardSeatMeter />
                    <Divider style={{ margin: '1rem 0' }} />
                </>
            ) : null}
            <Row align='top' style={{ width: '100%' }} wrap={false}>
                <Col flex='auto'>
                    <Typography.Title id='settings-account-plans-add-seats' level={5}>
                        Add Seats to Plan
                    </Typography.Title>
                    <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                        Add as many seats as you need to work with your team. Seat changes are prorated and billed
                        immediately.
                    </Typography.Paragraph>
                </Col>
            </Row>
            <Row align='middle' gutter={[0, 12]} style={{ width: '100%' }} wrap>
                <Col lg='auto' md='auto' xl='auto' xs={24} xxl='auto'>
                    <PlanSelector interval={interval} onChange={setPlan} value={plan} />
                </Col>
                <Col lg='none' md='none' xl='none' xs={12} xxl='none'>
                    <SeatQuantityInput
                        onChange={(value) => {
                            if (value !== 0) {
                                setSeats(value);
                            }
                        }}
                        value={seats}
                    />
                </Col>
                <Col lg='none' md='none' style={{ textAlign: 'right' }} xl='none' xs={12} xxl='none'>
                    <BillingIntervalToggle onChange={setInterval} value={interval} />
                </Col>
            </Row>
            {educationDiscountStatus === 'unverified_edu' ? (
                <PlatformAlert
                    description='Your account has a .edu email address, but it is not verified yet. You will receive the default plan price until your email is verified.'
                    icon={<TbInfoCircle />}
                    message='Verify your .edu email for education pricing'
                    showIcon
                    styles={{ marginTop: '0.75rem' }}
                    type='warning'
                />
            ) : null}
            {educationDiscountStatus === 'verified_edu' && educationDiscountPreview.hasDiscount ? (
                <PlatformAlert
                    description='Your verified .edu email qualifies for the education discount shown below.'
                    icon={<TbCircleDashedCheck />}
                    message='Education Pricing Applied'
                    showIcon
                    styles={{ marginTop: '0.75rem' }}
                    type='success'
                />
            ) : null}
            {educationDiscountStatus === 'verified_edu' && !educationDiscountPreview.hasDiscount ? (
                <PlatformAlert
                    description='The selected plan already costs less than education pricing, so the default price applies.'
                    message='Best Available Price Shown'
                    status='info'
                    styles={{ marginTop: '0.75rem' }}
                />
            ) : null}
            <CostPreview
                educationDiscountStatus={educationDiscountStatus}
                interval={interval}
                plan={plan}
                seats={seats}
            />
            <CheckoutButton
                disabled={loading || freeTrialLoading}
                loading={loading}
                onCheckout={handleCheckout}
                plan={plan}
            />
            {PLAN_CONFIG[plan].requiresEdu && !educationEligible ? (
                <PlatformAlert
                    description='The Education Plan requires your account to have a verified .edu email address.'
                    icon={<TbInfoCircle />}
                    message='Educational email required'
                    showIcon
                    status='info'
                    styles={{ marginTop: '1rem' }}
                />
            ) : null}
            <Divider style={{ margin: '1rem 0' }} />
            <div style={{ textAlign: 'center' }}>
                <small style={{ color: token.colorTextTertiary }}>
                    <TbInfoCircle style={{ marginRight: '0.25rem', verticalAlign: '-0.1rem' }} />
                    Please visit our{' '}
                    <a href='https://countercyclical.io/pricing' rel='noopener noreferrer' target='_blank'>
                        Pricing
                        <TbArrowUpRight style={{ marginLeft: '0.125rem', verticalAlign: '-0.1rem' }} />
                    </a>{' '}
                    page to see a list of feature breakdowns by plan.
                </small>
            </div>
            {isOnboarding ? (
                <div style={{ marginTop: '1.5rem', textAlign: 'center', width: '100%' }}>
                    <Button
                        disabled={loading || freeTrialLoading}
                        loading={freeTrialLoading}
                        onClick={handleContinueWithFreeTrial}
                        type='dashed'
                    >
                        <TbCalendarClock size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                        Continue with Free Trial
                        <TbArrowRight
                            size='1rem'
                            style={{ color: token.colorTextTertiary, verticalAlign: '-0.175rem' }}
                        />
                    </Button>
                </div>
            ) : null}
        </>
    );
};

export default PlanCheckout;
